import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { API_URL } from '../config/apiBase'
const TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_CACHE_KEY = 'user_cache'
const CACHE_DURATION = 20 * 60 * 1000 // 20 minutes in milliseconds

type User = {
  id: string
  email?: string
  [key: string]: any
}

type AuthContextValue = {
  token: string | null
  user: User | null
  loading: boolean
  signup: (args: { email: string; password: string; username?: string }) => Promise<{ ok: boolean; error?: string; autoLoggedIn?: boolean }>
  signin: (args: { email: string; password: string }) => Promise<{ ok: boolean; error?: string; user?: User }>
  signout: () => Promise<void>
  deleteAccount: () => Promise<{ ok: boolean; error?: string }>
  refreshToken: () => Promise<{ ok: boolean; error?: string }>
  updateUserProfile: (userData: Partial<User>) => Promise<{ ok: boolean; error?: string }>
  clearUserCache: () => void
  /** Re-fetch profile from /auth/user (clears stale client cache first) */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: any }) {
  const [token, setToken] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const persistTokens = useCallback((accessToken: string | null, refreshToken?: string | null) => {
    setToken(accessToken)
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken)
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
      }
    } else {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_CACHE_KEY)
    }
  }, [])

  const persistToken = useCallback((t: string | null) => {
    persistTokens(t)
  }, [persistTokens])

  const cacheUser = useCallback((userData: User) => {
    const cacheData = {
      user: userData,
      timestamp: Date.now()
    }
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(cacheData))
  }, [])

  const getCachedUser = useCallback((): User | null => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY)
      if (!cached) return null
      
      const { user, timestamp } = JSON.parse(cached)
      const now = Date.now()
      
      // Check if cache is still valid (within 20 minutes)
      if (now - timestamp < CACHE_DURATION) {
        return user
      }
      
      // Cache expired, remove it
      localStorage.removeItem(USER_CACHE_KEY)
      return null
    } catch {
      localStorage.removeItem(USER_CACHE_KEY)
      return null
    }
  }, [])

  const updateUserProfile = useCallback(async (userData: Partial<User>): Promise<{ ok: boolean; error?: string }> => {
    try {
      if (!token) {
        return { ok: false, error: 'No authentication token' };
      }

      const res = await fetch(`${API_URL}/users/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        return { ok: false, error: errorData.error || 'Failed to update profile' };
      }

      // Update local user state
      setUser(prev => {
        const updated = prev ? { ...prev, ...userData } : null
        // Cache the updated user data immediately
        if (updated) {
          cacheUser(updated);
        }
        return updated
      });

      // Force refresh user data from API to ensure consistency
      if (userData.has_finished_onboarding !== undefined) {
        // Clear cache first
        localStorage.removeItem(USER_CACHE_KEY)
        // Then fetch fresh data directly
        try {
          const res = await fetch(`${API_URL}/auth/user`, { headers: { Authorization: `Bearer ${token}` } })
          if (res.ok) {
            const json = await res.json()
            setUser(json.user)
            cacheUser(json.user)
          }
        } catch (error) {
          console.error('Error refreshing user data:', error)
        }
      }

      return { ok: true };
    } catch (error) {
      return { ok: false, error: 'Network error' };
    }
  }, [token, user, cacheUser]);

  const refreshToken = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (isRefreshing) {
      // If already refreshing, wait for it to complete
      return new Promise((resolve) => {
        const checkRefreshing = () => {
          if (!isRefreshing) {
            resolve({ ok: !!token })
          } else {
            setTimeout(checkRefreshing, 100)
          }
        }
        checkRefreshing()
      })
    }

    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshTokenValue) {
      return { ok: false, error: 'No refresh token available' }
    }

    setIsRefreshing(true)
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      })
      
      const data = await res.json()
      if (!res.ok) {
        // Refresh token is invalid, clear everything
        persistTokens(null, null)
        setUser(null)
        return { ok: false, error: data.error || 'Token refresh failed' }
      }

      // Update tokens
      persistTokens(data.session.access_token, data.session.refresh_token)
      await fetchUser(data.session.access_token)
      return { ok: true }
    } catch (error: any) {
      persistTokens(null, null)
      setUser(null)
      return { ok: false, error: error.message || 'Token refresh failed' }
    } finally {
      setIsRefreshing(false)
    }
  }, [isRefreshing, persistTokens, cacheUser])

  const fetchUser = useCallback(async (t: string) => {
    async function doFetch(allowRetry: boolean): Promise<void> {
      const res = await fetch(`${API_URL}/auth/user`, { headers: { Authorization: `Bearer ${t}` } })
      if (!res.ok) {
        // Try to parse error body
        let body: any = null
        try { body = await res.json() } catch {}
        const isInvalidToken = res.status === 401 || body?.error === 'Invalid token'
        
        if (isInvalidToken && allowRetry) {
          // Try to refresh token first
          const refreshResult = await refreshToken()
          if (refreshResult.ok) {
            // Retry with new token
            const newToken = localStorage.getItem(TOKEN_KEY)
            if (newToken) {
              return doFetch(false)
            }
          }
          // If refresh failed, try one more time with original token
          await new Promise((r) => setTimeout(r, 300))
          return doFetch(false)
        }
        throw new Error(body?.error || 'Failed to fetch user')
      }
      const json = await res.json()
      setUser(json.user)
      cacheUser(json.user)
    }
    await doFetch(true)
  }, [cacheUser, refreshToken])

  // Handle Supabase-style hash params like
  // #access_token=...&expires_in=...&refresh_token=...&token_type=bearer&type=signup
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : ''
    if (!hash || hash.length < 2) return

    const params = new URLSearchParams(hash.slice(1))
    const accessToken = params.get('access_token')
    if (!accessToken) return

    ;(async () => {
      try {
        persistToken(accessToken)
        await fetchUser(accessToken)
      } catch (_e) {
        // If token invalid, clear it
        persistToken(null)
        setUser(null)
      } finally {
        // Clean the URL hash so tokens aren't kept around
        if (typeof window !== 'undefined') {
          const cleanUrl = window.location.pathname + window.location.search
          window.history.replaceState(null, '', cleanUrl)
        }
        setLoading(false)
      }
    })()
  // We intentionally exclude fetchUser/persistToken from deps to avoid re-running
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY)
    if (!t) {
      setLoading(false)
      return
    }

    // Check if we have a valid cached user first
    const cachedUser = getCachedUser()
    if (cachedUser) {
      setToken(t)
      setUser(cachedUser)
      setLoading(false)
      return
    }

    // No valid cache, fetch from API
    ;(async () => {
      try {
        await fetchUser(t)
        setToken(t)
      } catch (_e) {
        // If fetchUser failed, try to refresh token
        const refreshResult = await refreshToken()
        if (!refreshResult.ok) {
          // Refresh failed, clear everything
          persistTokens(null, null)
          setUser(null)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [fetchUser, getCachedUser, refreshToken, persistTokens])

  const signup = useCallback(async ({ email, password, username }: { email: string; password: string; username?: string }) => {
    const res = await fetch(`${API_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, username }),
    })
    const data = await res.json()
    console.log('Signup response:', data) // Debug log
    if (!res.ok) return { ok: false, error: data.error || 'Signup failed' }
    
    let autoLoggedIn = false
    
    // If signup successful and we have a session, automatically log in
    if (data.session?.access_token) {
      console.log('Session token found, logging in automatically') // Debug log
      const accessToken = data.session.access_token
      const refreshToken = data.session.refresh_token
      persistTokens(accessToken, refreshToken)
      await fetchUser(accessToken)
      autoLoggedIn = true
    } else {
      console.log('No session token in response - email confirmation may be required') // Debug log
      // If no session token, try to sign in automatically with the same credentials
      try {
        const signinRes = await fetch(`${API_URL}/auth/signin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })
        const signinData = await signinRes.json()
        if (signinRes.ok && signinData.session?.access_token) {
          console.log('Auto signin successful after signup') // Debug log
          const accessToken = signinData.session.access_token
          const refreshToken = signinData.session.refresh_token
          persistTokens(accessToken, refreshToken)
          await fetchUser(accessToken)
          autoLoggedIn = true
        } else {
          console.log('Auto signin failed after signup') // Debug log
        }
      } catch (error) {
        console.log('Auto signin error after signup:', error) // Debug log
      }
    }
    
    return { ok: true, autoLoggedIn }
  }, [persistTokens, fetchUser])

  const signin = useCallback(async ({ email, password }: { email: string; password: string }) => {
    const res = await fetch(`${API_URL}/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error || 'Login failed' }
    const accessToken = data.session?.access_token as string
    const refreshToken = data.session?.refresh_token as string
    if (!accessToken) return { ok: false, error: 'Missing token' }
    persistTokens(accessToken, refreshToken)
    await fetchUser(accessToken)
    
    // Get the user data after fetching
    const userRes = await fetch(`${API_URL}/auth/user`, { 
      headers: { Authorization: `Bearer ${accessToken}` } 
    })
    const userData = await userRes.json()
    
    return { ok: true, user: userData.user }
  }, [persistTokens, fetchUser])

  const signout = useCallback(async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/signout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      }
    } finally {
      persistTokens(null, null)
      setUser(null)
      localStorage.removeItem(USER_CACHE_KEY)
    }
  }, [token, persistTokens])

  const deleteAccount = useCallback(async () => {
    if (!token) return { ok: false, error: 'Not authenticated' }
    const res = await fetch(`${API_URL}/auth/delete-account`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json()
    if (!res.ok) return { ok: false, error: data.error || 'Delete failed' }
    persistTokens(null, null)
    setUser(null)
    localStorage.removeItem(USER_CACHE_KEY)
    return { ok: true }
  }, [token, persistTokens])

  const clearUserCache = useCallback(() => {
    localStorage.removeItem(USER_CACHE_KEY)
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY)
    if (!t) return
    localStorage.removeItem(USER_CACHE_KEY)
    try {
      await fetchUser(t)
      setToken(t)
    } catch {
      /* keep existing user state */
    }
  }, [fetchUser])

  const value = useMemo<AuthContextValue>(() => ({ token, user, loading, signup, signin, signout, deleteAccount, refreshToken, updateUserProfile, clearUserCache, refreshUser }), [token, user, loading, signup, signin, signout, deleteAccount, refreshToken, updateUserProfile, clearUserCache, refreshUser])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}


